var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Util } from "../Util";
import { EventFiringClient } from "../EventFiringClient";
import { AuviousError } from "../AuviousError";
import { Event } from "./Event";
import { connect as connectToMqtt } from "./Mqtt";
/**
 * Event Handlers to be fired when applicable.
 */
export class EventClientHandlers {
}
class EventClientSubscription {
    constructor(eventClient, handlers) {
        this.mEventClient = eventClient;
        this.mId = Util.uuidgen();
        this.mHandlers = handlers;
    }
    id() {
        return this.mId;
    }
    dispose() {
        this.mEventClient.dispose(this);
    }
    handlers() {
        return this.mHandlers;
    }
}
var EventClientState;
(function (EventClientState) {
    EventClientState[EventClientState["Connecting"] = 0] = "Connecting";
    EventClientState[EventClientState["Connected"] = 1] = "Connected";
    EventClientState[EventClientState["Disconnecting"] = 2] = "Disconnecting";
    EventClientState[EventClientState["Disconnected"] = 3] = "Disconnected";
})(EventClientState || (EventClientState = {}));
/**
 * Event client implementation for auvious' services.
 *
 * Setup to work via the mqtt protocol with the paho-mqtt package.
 *
 * More info: http://www.eclipse.org/paho/clients/js
 */
export class EventClient extends EventFiringClient {
    constructor(mqttUrl, assetsLocation, host, port, path = "/mqtt", sslEnabled, user, pass, refreshConnectionOnUpdatedCredentials = true) {
        super("EventClient", null);
        this.assetsLocation = assetsLocation;
        this.refreshConnectionOnUpdatedCredentials = refreshConnectionOnUpdatedCredentials;
        this._clientId = Util.uuidgen(Date.now().toString());
        this._subscriptions = new Map();
        this.state = EventClientState.Disconnected;
        if (!host) {
            throw new Error("Invalid host: " +
                host +
                ', set the host string in the first arg. eg "localhost"');
        }
        else if (!port) {
            throw new Error("Invalid port: " +
                port +
                ", set the port number in the second arg. eg 8383");
        }
        this._sslEnabled = sslEnabled;
        this._username = user;
        this._password = pass;
        const scheme = this._sslEnabled ? "wss://" : "ws://";
        this._connectionURL = mqttUrl || scheme + host + ":" + port + path;
    }
    /**
     * Exposes setEventHandlers of EventFiringClient's implementation to the public api.
     */
    setEventHandlers(handlers) {
        const subscription = new EventClientSubscription(this, handlers);
        this._subscriptions.set(subscription.id(), subscription);
        return subscription;
    }
    /**
     * Exposes cleanEventHandlers of EventFiringClient's implementation to the public api.
     */
    cleanEventHandlers() {
        this._cleanEventHandlers();
    }
    /**
     * Connect the client with the server.
     *
     * Events are fired via the callbacks of the EventClientHandlers argument.
     */
    connect(handlers) {
        return __awaiter(this, void 0, void 0, function* () {
            this.log.info("Connect to server: " + this._connectionURL);
            let subscription = null;
            if (handlers) {
                subscription = this.setEventHandlers(handlers);
            }
            if (this.state === EventClientState.Connected ||
                this.state === EventClientState.Connecting) {
                return subscription;
            }
            return new Promise((resolve, reject) => {
                let closeCount = 0;
                const nop = () => { };
                let res = (arg) => {
                    resolve(arg);
                    res = nop;
                };
                let rej = (arg) => {
                    reject(arg);
                    rej = nop;
                };
                this.state = EventClientState.Connecting;
                this._mqttOptions = {
                    clientId: this._clientId,
                    keepalive: 10,
                    clean: false,
                    reconnectPeriod: 4000,
                };
                if (this._username) {
                    this._mqttOptions.username = this._username;
                }
                if (this._password) {
                    this._mqttOptions.password = this._password;
                }
                connectToMqtt(this._connectionURL, this._mqttOptions, this.assetsLocation).then((mqttClient) => {
                    this._mqttClient = mqttClient;
                    this._mqttClient.addListener("connect", () => {
                        closeCount = 0;
                        if (this.state === EventClientState.Connecting) {
                            this._eventHandlers.onConnected();
                        }
                        this.state = EventClientState.Connected;
                        res(subscription);
                    });
                    // Emitted when the client cannot connect (i.e. connack rc != 0)
                    // or when a parsing error occurs.
                    this._mqttClient.addListener("error", (error) => {
                        const ev = new AuviousError(error);
                        // todo: check wrong connection
                        if (this.state === EventClientState.Connecting) {
                            this._eventHandlers.onConnectedFailed(ev);
                        }
                        rej(ev);
                    });
                    this._mqttClient.addListener("close", () => {
                        if (closeCount++ === 0) {
                            this._eventHandlers.onDisconnected();
                        }
                        else if (closeCount === 3 &&
                            this.state !== EventClientState.Disconnecting) {
                            rej(AuviousError.create("Failed to connect to events server."));
                            if (this.state === EventClientState.Connecting) {
                                this._eventHandlers.onConnectedFailed(AuviousError.create("Failed to connect to events server."));
                            }
                        }
                        this.state = EventClientState.Disconnected;
                    });
                    this._mqttClient.addListener("offline", () => {
                        this._eventHandlers.onDisconnected();
                    });
                    this._mqttClient.addListener("message", (_topic, message) => {
                        this._eventHandlers.onMessageReceived(new Event({
                            payloadString: message,
                        }));
                    });
                });
            });
        });
    }
    /**
     * Disconnect the client from the server.
     *
     * The current event handlers are not cleaned.
     * They can be reused from a followup .connect() without args.
     */
    disconnect() {
        this.log.debug("disconnect()");
        this.state = EventClientState.Disconnecting;
        return new Promise((resolve) => {
            const client = this._mqttClient;
            this._mqttClient = null;
            client ? client.end(false, resolve) : resolve();
        });
    }
    /**
     * Appends metadata into the payload.
     *
     * @param data the message data to send
     */
    // protected generateMessagePayload(message): any {
    //   return {
    //     message,
    //     // metadata to send along with the payload
    //     id: Util.uuidgen(),
    //     sentAt: new Date().getTime(),
    //     senderId: this.clientId,
    //   };
    // }
    /**
     * Sends a message to the server on a specific topic channel
     * @param {string} topic - The name of the topic to which the message is to be sent.
     * @param {string|object} data - The message data to be sent.
     * @param {number} qos The Quality of Service used to deliver the message.
     * 			0 Best effort (default).
     *     	1 At least once.
     *     	2 Exactly once.
     * @param {boolean} retained If true, the message is to be retained by the server and delivered
     *                     to both current and future subscriptions.
     *                     If false the server only delivers the message to current subscribers,
     *                     this is the default for new Messages.
     *                     A received message has the retained boolean set to true if the message
     *                     was published with the retained boolean set to true.
     */
    // public sendMessage(
    //   topic: string,
    //   messageContent: any,
    //   qos: 0 | 1 | 2 = 0,
    //   retained: boolean = false
    // ): Promise<Event> {
    //   const messageContentWithMeta = this.generateMessagePayload(messageContent);
    //   const message = Serializer.serialize(messageContentWithMeta);
    //   this.log.info("messaging to topic: " + topic, message);
    //   // todo: check if ok
    //   const evmsg = new Event({ payload: message });
    //   this._pendingMessageSentEvents[evmsg.id] = evmsg;
    //   this._eventHandlers.onMessageSent(evmsg);
    //   // wait for this key to be assigned at _pendingMessageSentEvents
    //   // during onMessageSent event, to resolve the promise.
    //   return new Promise<Event>((resolve, reject) => {
    //     const options: IClientPublishOptions = {
    //       qos,
    //       retain: retained,
    //     };
    //     this._mqttClient.publish(topic, message, options, (err) => {
    //       if (err) {
    //         reject(
    //           AuviousError.create(
    //             `Unexpected delay on message sent. Promise timed out after ${EventClient.PROMISE_TIMEOUT_MILLISEC} ms.`,
    //             99
    //           )
    //         );
    //       }
    //       // resolve when the messageSent event is fired.
    //       const ev2 = this._pendingMessageSentEvents[messageContentWithMeta.id];
    //       // clean the event from the _pendingMessageSentEvents.
    //       delete this._pendingMessageSentEvents[messageContentWithMeta.id];
    //       resolve(ev2);
    //     });
    //   });
    // }
    /**
     * Subscribe to a topic to listen to receive events sent to that.
     */
    subscribe(topic) {
        this.log.info("subscribing to topic: " + topic);
        return new Promise((resolve, reject) => {
            const opts = {
                qos: 1,
            };
            if (!this._mqttClient) {
                this.log.warn("Subscribe failed. MQTT not connected. Please connect first.");
                return reject(new Error("not-connected"));
            }
            this._mqttClient.subscribe(topic, opts, (err, granted) => {
                this.log.debug("subscribe callback called", { err, granted });
                if (err) {
                    const ev = new AuviousError(err);
                    this._eventHandlers.onSubscribedFailed(ev);
                    reject(ev);
                }
                else if (granted) {
                    const ev = new Event({ topic });
                    this._eventHandlers.onSubscribed(ev);
                    resolve(ev);
                }
            });
        });
    }
    /**
     * Unsubscribe from a topic to listen to stop receiving events sent to that.
     */
    unsubscribe(topic) {
        this.log.info("unsubscribing from topic: " + topic);
        return new Promise((resolve, reject) => {
            if (!this._mqttClient) {
                this.log.warn("Unsubscribe failed. MQTT not connected. Please connect first.");
                return reject(new Error("not-connected"));
            }
            this._mqttClient.unsubscribe(topic, (err) => {
                if (!err) {
                    const ev = new Event({ topic });
                    this._eventHandlers.onUnsubscribed(ev);
                    resolve(ev);
                }
                else {
                    const ev = new AuviousError(err);
                    this._eventHandlers.onUnsubscribedFailed(ev);
                    reject(ev);
                }
            });
        });
    }
    get subscriptionHandlers() {
        return {
            onConnected: () => this._subscriptions.forEach((sub) => {
                try {
                    if (sub.handlers().onConnected) {
                        sub.handlers().onConnected();
                    }
                }
                catch (error) {
                    this.log.error(error);
                }
            }),
            onConnectedFailed: (ev) => this._subscriptions.forEach((sub) => {
                try {
                    if (sub.handlers().onConnectedFailed) {
                        sub.handlers().onConnectedFailed(ev);
                    }
                }
                catch (error) {
                    this.log.error(error);
                }
            }),
            onDisconnected: () => this._subscriptions.forEach((sub) => {
                try {
                    if (sub.handlers().onDisconnected) {
                        sub.handlers().onDisconnected();
                    }
                }
                catch (error) {
                    this.log.error(error);
                }
            }),
            onSubscribed: (ev) => this._subscriptions.forEach((sub) => {
                try {
                    if (sub.handlers().onSubscribed) {
                        sub.handlers().onSubscribed(ev);
                    }
                }
                catch (error) {
                    this.log.error(error);
                }
            }),
            onSubscribedFailed: (ev) => this._subscriptions.forEach((sub) => {
                try {
                    if (sub.handlers().onSubscribedFailed) {
                        sub.handlers().onSubscribedFailed(ev);
                    }
                }
                catch (error) {
                    this.log.error(error);
                }
            }),
            onUnsubscribed: (ev) => this._subscriptions.forEach((sub) => {
                try {
                    if (sub.handlers().onUnsubscribed) {
                        sub.handlers().onUnsubscribed(ev);
                    }
                }
                catch (error) {
                    this.log.error(error);
                }
            }),
            onUnsubscribedFailed: (ev) => this._subscriptions.forEach((sub) => {
                try {
                    if (sub.handlers().onUnsubscribedFailed) {
                        sub.handlers().onUnsubscribedFailed(ev);
                    }
                }
                catch (error) {
                    this.log.error(error);
                }
            }),
            onMessageReceived: (ev) => this._subscriptions.forEach((sub) => {
                try {
                    if (sub.handlers().onMessageReceived) {
                        sub.handlers().onMessageReceived(ev);
                    }
                }
                catch (error) {
                    this.log.error(error);
                }
            }),
            onMessageSent: (ev) => this._subscriptions.forEach((sub) => {
                try {
                    if (sub.handlers().onMessageSent) {
                        sub.handlers().onMessageSent(ev);
                    }
                }
                catch (error) {
                    this.log.error(error);
                }
            }),
            onError: (ev) => this._subscriptions.forEach((sub) => {
                try {
                    if (sub.handlers().onError) {
                        sub.handlers().onError(ev);
                    }
                }
                catch (error) {
                    this.log.error(error);
                }
            }),
        };
    }
    get _defaultHandlers() {
        return {
            onConnected: () => {
                this.log.info("onConnected");
                this.subscriptionHandlers.onConnected();
            },
            onConnectedFailed: (ev) => {
                this.log.error("onConnectedFailed", ev);
                this.subscriptionHandlers.onConnectedFailed(ev);
            },
            onDisconnected: () => {
                this.log.info("onDisconnected");
                this.subscriptionHandlers.onDisconnected();
            },
            onSubscribed: (ev) => {
                this.log.info("onSubscribed", ev);
                this.subscriptionHandlers.onSubscribed(ev);
            },
            onSubscribedFailed: (ev) => {
                this.log.error("onSubscribedFailed", ev);
                this.subscriptionHandlers.onSubscribedFailed(ev);
            },
            onUnsubscribed: (ev) => {
                this.log.info("onUnsubscribed", ev);
                this.subscriptionHandlers.onUnsubscribed(ev);
            },
            onUnsubscribedFailed: (ev) => {
                this.log.error("onUnsubscribedFailed", ev);
                this.subscriptionHandlers.onUnsubscribedFailed(ev);
            },
            onMessageReceived: (ev) => {
                this.log.info("onMessageReceived", ev);
                this.subscriptionHandlers.onMessageReceived(ev);
            },
            onMessageSent: (ev) => {
                this.log.info("onMessageSent", ev);
                this.subscriptionHandlers.onMessageSent(ev);
            },
            onError: (ev) => {
                this.log.error("onError", ev);
                this.subscriptionHandlers.onError(ev);
            },
        };
    }
    updateCredentials(username, password) {
        return __awaiter(this, void 0, void 0, function* () {
            this.log.debug(`updateCredentials('${username}', '${password.substr(0, 10)}')`);
            this._username = username;
            this._password = password;
            if (this._mqttOptions != null) {
                this._mqttOptions.username = username;
                this._mqttOptions.password = password;
            }
            if (this._mqttClient != null) {
                this._mqttClient.options.setUsername(username);
                this._mqttClient.options.setPassword(password);
                if (this.refreshConnectionOnUpdatedCredentials) {
                    yield this.disconnect();
                    yield this.connect();
                }
            }
        });
    }
    dispose(subscription) {
        this._subscriptions.delete(subscription.id());
    }
    get initialized() {
        return !!this._connectionURL;
    }
    connected() {
        return __awaiter(this, void 0, void 0, function* () {
            return this._mqttClient ? this._mqttClient.getConnected() : false;
        });
    }
}
EventClient.PROMISE_TIMEOUT_MILLISEC = 5000;
//# sourceMappingURL=EventClient.js.map